import React, { Component } from "react";
import { Link } from "react-router";
import { Row, Col, Card, Table, Tooltip, Tag, Button } from "antd";
import { LinkOutlined } from "@ant-design/icons";
import Helmet from "react-helmet";
import PatentsRelated from "./patents-related";
import LoadingWrapper from "../common/loading-wrapper";
import UserAccess from "../common/user-access";
import TimeLine from "../common/timeline";
import ZendeskNewRequestLink from "../common/links/zendesk-new-request-link";
import { getCase } from "../../services/api-maxval";
import { toDateString, toSetString, toPatentDetailLink, toGooglePatentLink, toItemLitigationSearchLink } from "../../helpers/converters";
import { PUBLIC_URL } from "../../config";
import { endsWith, isArray, sortBy } from "lodash";

const styles = {
  body: {
    width: "100%",
    padding: 30
  },
  fullWidthStyle: {
    width: "100%",
    padding: 64,
    textAlign: "center"
  },
  gutter: {
    marginTop: 16
  },
  paddingBottomSm: {
    paddingBottom: 6
  },
  paddingBottomMd: {
    paddingBottom: 30
  },
  addonTag: {
    cursor: "default"
  },
  reportErrorLink: {
    float: "right",
    fontWeight: 400,
    fontSize: "0.7em"
  }
};

class Litigation extends Component {
  state = {
    loading: true,
    profile: {},
    isSelected: () => undefined
  };

  componentDidMount() {
    this._isMounted = true;
    this._getCase(this.props.params.casenum, this.props.params.courtname);
  }

  UNSAFE_componentWillReceiveProps() {
    if (this._isMounted)
      this.setState(
        {
          profile: {},
          loading: true
        },
        () => {
          this._getCase(this.props.params.casenum, this.props.params.courtname);
        }
      );
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  render() {
    const { isSelected, profile } = this.state;
    const { casenum, courtname } = this.props.params;
    let pageTitle = casenum;
    let keywords = [`${casenum}`, `${courtname}`, `Litigation Case`, `Patent Litigation`, `Lawsuit`, `Patent Lawsuit`];
    let description = `Case docket and documents for ${casenum}, ${courtname}.`;
    if (profile.case_no) {
      // after data fetching
      const plaintiffTitle = isArray(profile.plaintiff) ? `${profile.plaintiff[0]}${profile.plaintiff.length > 1 ? " et al." : ""}` : profile.plaintiff;
      const defendantTitle = isArray(profile.defendant) ? `${profile.defendant[0]}${profile.defendant.length > 1 ? " et al." : ""}` : profile.defendant;
      if (plaintiffTitle && defendantTitle) {
        // new title and keywords if both plaintiff and defendant are not undefined
        pageTitle = `${casenum} - ${plaintiffTitle} v. ${defendantTitle}`;
        if (isArray(profile.plaintiff)) {
          profile.plaintiff.forEach(p => keywords.push(p));
        } else {
          keywords.push(profile.plaintiff);
        }
        if (isArray(profile.defendant)) {
          profile.defendant.forEach(d => keywords.push(d));
        } else {
          keywords.push(profile.defendant);
        }
      }
      description = `Case docket and documents for ${pageTitle}, ${courtname}, filed at ${toDateString(profile.filed_date)}.`;
    }
    const helmet = {
      title: pageTitle,
      meta: [
        {
          name: "keywords",
          content: keywords.join(",")
        },
        {
          name: "description",
          content: description
        }
      ],
      link: [
        {
          rel: "canonical",
          href: encodeURI(PUBLIC_URL + decodeURI(this.props.router.location.pathname))
        }
      ]
    };
    const docketColumns = [
      {
        dataIndex: "proceeding",
        key: "proceeding",
        title: "Text"
      }
    ];
    const INDUSTRY_MAP = {
      T: "High-Tech",
      M: "Medical",
      O: "Other"
    };
    const dates = this._transformTimelineData(profile);
    let timelineNumMonths = 36;
    if (dates && dates.length) {
      const dStart = new Date(dates[0].date);
      const dEnd = new Date(dates[dates.length - 1].date);
      let diffMonths;
      diffMonths = (dEnd.getFullYear() - dStart.getFullYear()) * 12;
      diffMonths -= dStart.getMonth();
      diffMonths += dEnd.getMonth();
      timelineNumMonths = Math.max(diffMonths + 2, 36);
    }
    return (
      <div style={styles.body}>
        <Helmet {...helmet} />
        <LoadingWrapper loading={this.state.loading}>
          {profile && Object.keys(profile).length ? (
            <div>
              <h2>
                {pageTitle}
                {profile.pacer_link ? (
                  <Tooltip title="View PACER">
                    <a href={profile.pacer_link} target="_blank" rel="noopener noreferrer" style={{ marginLeft: 4 }}>
                      <LinkOutlined />
                    </a>
                  </Tooltip>
                ) : null}
                <ZendeskNewRequestLink text="Report Error" subjectPrefix="Correction needed for page" style={styles.reportErrorLink} className="hidden-sm" />
              </h2>

              <Card title="Detail" style={styles.gutter}>
                <Row>
                  <Col span={24} md={12} lg={6} style={styles.paddingBottomMd}>
                    <h4> {"Case Number"} </h4>
                    {profile.case_no || "N/A"}
                  </Col>
                  <Col span={24} md={12} lg={6} style={styles.paddingBottomMd}>
                    <h4> {"Current Status"} </h4>
                    {profile.status || "N/A"}
                  </Col>
                  <Col span={24} md={12} lg={6} style={styles.paddingBottomMd}>
                    <h4> {"Cause of Action"} </h4>
                    {isArray(profile.cause_of_action) ? toSetString(profile.cause_of_action) : profile.cause_of_action || "N/A"}
                  </Col>
                  <Col span={24} md={12} lg={6} style={styles.paddingBottomMd}>
                    <h4> {"Related Case(s)"} </h4>
                    {/* handle both old and new data structures due to potential cache delays. */}
                    {
                      profile.related_cases && profile.related_cases.length > 0
                        ? (typeof profile.related_cases[0] === 'object'
                          ? this._renderRelatedCases(profile.related_cases) || "N/A"
                          : toSetString(profile.related_cases) || "N/A")
                        : "N/A"
                    }
                  </Col>
                </Row>
                <Row>
                  <Col span={24} md={12} lg={6} style={styles.paddingBottomMd}>
                    <h4> {"Court"} </h4>
                    {profile.court || "N/A"}
                  </Col>
                  <Col span={24} md={12} lg={6} style={styles.paddingBottomMd}>
                    <h4> {"Plaintiff(s)"} </h4>
                    {toSetString(profile.plaintiff) || "N/A"}
                    {profile.entity_type && (
                      <Tag color="orange" style={styles.addonTag}>
                        {profile.entity_type}
                      </Tag>
                    )}
                    {profile.npe_aggregator &&
                      profile.npe_aggregator.length > 0 &&
                      profile.npe_aggregator.map(name => (
                        <Tag key={name} color="red" style={styles.addonTag}>
                          {name}
                        </Tag>
                      ))}
                    {profile.has_tplf && (
                      <Tag color="green" style={styles.addonTag}>
                        {"Third Party Financing"}
                      </Tag>
                    )}
                  </Col>
                  <Col span={24} md={12} lg={6} style={styles.paddingBottomMd}>
                    <h4>
                      {" "}
                      {"Defendant(s)"}{" "}
                      {profile.sme_annotation && profile.sme_annotation.indexOf("SME") >= 0 && (
                        <Tag color="green" style={styles.addonTag}>
                          {"SME"}
                        </Tag>
                      )}
                    </h4>
                    {toSetString(profile.defendant) || "N/A"}
                  </Col>
                  <Col span={24} md={12} lg={6} style={styles.paddingBottomMd}>
                    <h4> {"Judge"} </h4>
                    {profile.judge || "N/A"}
                  </Col>
                </Row>

                <Row>
                  <Col span={24} md={12} lg={6} style={styles.paddingBottomMd}>
                    <h4> {"Filing Date"} </h4>
                    {toDateString(profile.filed_date) || "N/A"}
                  </Col>
                  <Col span={24} md={12} lg={6} style={styles.paddingBottomMd}>
                    <h4> {"Termination Date"} </h4>
                    {toDateString(profile.closed_date) || "N/A"}
                  </Col>
                </Row>

                <UserAccess isPublicSignupAllowed={true} showHelp={true} helpText="Please Login to Reveal Involved Patents and Infringed Product" helpButtonSize="small">
                  <Row>
                    <Col span={24} md={12} lg={6} style={styles.paddingBottomMd}>
                      <h4> {"Patents in Case"} </h4>
                      {profile.patents
                        ? profile.patents.map(pn => (
                          <div key={pn}>
                            {toPatentDetailLink(pn)}
                            {toItemLitigationSearchLink(pn, null, "patents")}
                            {toGooglePatentLink(`US${pn}`)}
                          </div>
                        ))
                        : "N/A"}
                      {profile.industry && (
                        <Tag color="cyan" style={styles.addonTag}>
                          {INDUSTRY_MAP[profile.industry]}
                        </Tag>
                      )}
                    </Col>
                    <Col span={24} md={12} lg={18} style={styles.paddingBottomMd}>
                      <h4> {"Accused Product(s)"} </h4>
                      {/* handle both old and new data structures due to potential cache delays. */}
                      {
                        profile.product
                          ? (
                            Array.isArray(profile.product)
                              ? toSetString(profile.product) || "N/A"
                              : profile.product || "N/A"
                          )
                          : "N/A"
                      }
                    </Col>
                  </Row>
                </UserAccess>
              </Card>

              <Card title={"Dockets"} style={styles.gutter}>
                <Table
                  dataSource={(profile.dockets || []).map((d, i) => ({
                    ...d,
                    key: i
                  }))}
                  columns={docketColumns}
                  scroll={{ x: 800 }}
                  pagination={false}
                  size="small"
                />
              </Card>

              <Card title={"Timeline"} style={styles.gutter}>
                <TimeLine data={dates} importantLabels={dates.map(e => e.label)} numMonths={timelineNumMonths} cycleSize={4} isSelected={isSelected} />
              </Card>

              <Card title={"Patents In Case"} style={styles.gutter}>
                {profile.related_patents && <PatentsRelated data={profile.related_patents} />}
              </Card>
            </div>
          ) : (
            <div style={styles.fullWidthStyle}>
              <h2>{`Case ${this.props.params.casenum} Not Found`}</h2>
              <Button type="primary" style={styles.gutter} onClick={() => this.props.router.push("/ptab/caselist")}>
                {"New Search"}
              </Button>
            </div>
          )}
        </LoadingWrapper>
      </div>
    );
  }

  _transformTimelineData = data => {
    const datesArr = [];
    const excludes = ["creation_date", "last_modified_date"];
    if (data) {
      Object.entries(data).forEach(([key, value]) => {
        if (value != null && value != "" && endsWith(key, "_date") && !excludes.includes(key))
          datesArr.push({
            date: value,
            label: key
          });
      });
    }
    return sortBy(datesArr, "date");
  };

  _getCase = (casenum, courtname) => {
    getCase(casenum, courtname)
      .then(result => {
        if (result) {
          if (this._isMounted)
            this.setState({
              profile: result,
              loading: false
            });
        } else {
          if (this._isMounted)
            this.setState({
              loading: false
            });
        }
      })
      .catch(() => {
        if (this._isMounted)
          this.setState({
            loading: false
          });
      });
  };

  _renderRelatedCases = relatedCases => {
    if (relatedCases) {
      return relatedCases.map((elem, i) => {
        return (
          <div key={i}>
            <Link to={`/litigation/${elem.court}/case/${elem.case_no}`}>{elem.case_no}</Link>
          </div>
        );
      });
    }
  };
}

export default Litigation;
